import {
  Box,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import FacebookFeedPreview from "../../components/AdPreview/FacebookFeedPreview";
import FacebookStoryPreview from "../../components/AdPreview/FacebookStoryPreview";
import InstagramPostPreview from "../../components/AdPreview/InstagramPostPreview";
import InstagramStoryPreview from "../../components/AdPreview/InstagramStoryPreview";
import type { CreativeBlock } from "../types";
import {
  hasInstagramAccount,
  hasSquareCreativeSlot,
  hasVerticalCreativeSlot,
} from "../creativePreviewSlots";

type CreateCampaignFlowPreviewProps = {
  mobilePanel: "editor" | "preview";
  creatives: CreativeBlock[];
  fieldIds: string[];
  ctaText: string;
  websiteUrl: string;
  eventName: string;
  eventDescription: string;
};

export default function CreateCampaignFlowPreview({
  mobilePanel,
  creatives,
  fieldIds,
  ctaText,
  websiteUrl,
  eventName,
  eventDescription,
}: CreateCampaignFlowPreviewProps) {
  const hasInstagramPreview = useMemo(
    () =>
      creatives.some(
        (c) =>
          hasInstagramAccount(c) &&
          (hasSquareCreativeSlot(c) || hasVerticalCreativeSlot(c))
      ),
    [creatives]
  );

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    setTabIndex(hasInstagramPreview ? 1 : 0);
  }, [hasInstagramPreview]);

  const instagramPreviews = (
    <VStack align="stretch" spacing={6} w="full">
      {fieldIds.map((fieldId, index) => {
        const creative = creatives[index];
        if (!creative) return null;
        const showSquare = hasSquareCreativeSlot(creative);
        const showVertical = hasVerticalCreativeSlot(creative);
        const hasInstagram = hasInstagramAccount(creative);
        if (!hasInstagram || (!showSquare && !showVertical)) return null;

        return (
          <Stack key={`instagram-preview-${fieldId}`} spacing={6}>
            {showSquare && (
              <InstagramPostPreview
                username={creative.instagramAccount?.username ?? "Account"}
                message={creative.primaryText}
                ctaText={ctaText}
                mediaUrls={
                  creative.squareMediaUrl ? [creative.squareMediaUrl] : []
                }
                profilePictureUrl={
                  creative.instagramAccount?.profile_picture_url || undefined
                }
                mediaType="single"
                fullWidth={true}
              />
            )}
            {showVertical && (
              <InstagramStoryPreview
                username={creative.instagramAccount?.username ?? "Account"}
                ctaText={ctaText}
                mediaUrls={
                  creative.verticalMediaUrl ? [creative.verticalMediaUrl] : []
                }
                profilePictureUrl={
                  creative.instagramAccount?.profile_picture_url || undefined
                }
                mediaType="single"
                fullWidth={true}
              />
            )}
          </Stack>
        );
      })}
    </VStack>
  );

  const facebookPreviews = (
    <VStack align="stretch" spacing={6} w="full">
      {fieldIds.map((fieldId, index) => {
        const creative = creatives[index];
        if (!creative) return null;
        const showSquare = hasSquareCreativeSlot(creative);
        const showVertical = hasVerticalCreativeSlot(creative);
        if (!showSquare && !showVertical) return null;

        return (
          <Stack key={`facebook-preview-${fieldId}`} spacing={6}>
            {showSquare && (
              <FacebookFeedPreview
                message={creative.primaryText}
                ctaText={ctaText}
                mediaUrls={
                  creative.squareMediaUrl ? [creative.squareMediaUrl] : []
                }
                mediaType="single"
                pageName={creative.fbPage?.name ?? "Account"}
                profilePictureUrl={creative.fbPage?.picture || undefined}
                websiteUrl={websiteUrl}
                eventName={eventName}
                description={eventDescription}
                fullWidth={true}
              />
            )}
            {showVertical && (
              <FacebookStoryPreview
                message={creative.primaryText}
                ctaText={ctaText}
                mediaUrls={
                  creative.verticalMediaUrl ? [creative.verticalMediaUrl] : []
                }
                mediaType="single"
                pageName={creative.fbPage?.name ?? "Account"}
                profilePictureUrl={creative.fbPage?.picture || undefined}
                websiteUrl={websiteUrl}
                eventName={eventName}
                description={eventDescription}
                fullWidth={true}
              />
            )}
          </Stack>
        );
      })}
    </VStack>
  );

  const instagramNeedsAccountMessage = (
    <Text fontSize="sm" color="gray.600" textAlign="center" px={2} py={4}>
      Select an Instagram account in the creative editor to preview Instagram
      placements.
    </Text>
  );

  return (
    <Box
      flex={1}
      rounded="xl"
      alignSelf="stretch"
      display={{
        base: mobilePanel === "preview" ? "block" : "none",
        md: "block",
      }}
    >
      <Tabs
        variant="soft-rounded"
        colorScheme="gray"
        size="sm"
        index={tabIndex}
        onChange={setTabIndex}
      >
        <TabList bg="gray.200" rounded="lg" p={1} maxW="80%" mx="auto">
          <Tab
            _selected={{ bg: "white", shadow: "sm" }}
            rounded="md"
            width="50%"
          >
            <Text fontSize="xs">Facebook Preview</Text>
          </Tab>
          <Tab
            _selected={{ bg: "white", shadow: "sm" }}
            rounded="md"
            width="50%"
          >
            <Text fontSize="xs">Instagram Preview</Text>
          </Tab>
        </TabList>
        <TabPanels pt={4}>
          <TabPanel px={0}>{facebookPreviews}</TabPanel>
          <TabPanel px={0}>
            {hasInstagramPreview
              ? instagramPreviews
              : instagramNeedsAccountMessage}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
